/* Onglet Paramètres du CRM : champs obligatoires par étape, modèles de contrat (PDF) et modèles de messages. */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  BookOpenCheck, Calculator, Euro, CalendarClock, CheckCircle, ChevronDown, ChevronRight, Eye, FileSignature, Landmark, ListChecks, Loader2, Lock, Mail, Plus, RotateCcw, Save,
  Trash2, Upload, XCircle, Zap,
} from 'lucide-react';
import type { SharingOperation } from './types';
import type { TodoOperationSettings, TodoSettings } from '../../../supabase/functions/_shared/crmTodo';
import type { MessageTemplate, Role, TemplateKey } from './types';
import { CONTRACT_TEMPLATES, ENTITY_LABELS, EXTRA_CONTRACT_TEMPLATES, LEGACY_CONTRACT_TEMPLATE_FILES, ROLE_LABELS, TEMPLATE_VARIABLES } from './types';
import { type SepaSettings, DEFAULT_SEPA_SETTINGS, fetchSepaSettings, saveSepaSettings, sepaSettingsErrors } from './sepaSettings';
import {
  type SavingsEstimateSettings, DEFAULT_SAVINGS_SETTINGS, SAVINGS_VARIABLES, computeSavings, fetchSavingsSettings, formatEuros,
  renderSavingsSentence, saveSavingsSettings, savingsSettingsErrors,
} from './savingsEstimate';
import { SEPA_FORMATS, type SepaFormat } from '../../../supabase/functions/_shared/sepa';
import {
  fetchOperations, fetchTodoSettings, saveTodoSettings, sendDigestNow,
  fetchFieldRequirements, fetchTemplates, listContractTemplates, openStoredFile, saveFieldRequirements, saveTemplate, uploadContractTemplate,
} from './api';
import {
  REQUIREMENT_RULES, type RequirementOverrides, type RequirementStage, requirementStage, setRequirementOverrides,
} from './validation';
import { STAGES, WAITING_LABELS } from './types';
import type { WaitingOn } from './types';
import {
  DEFAULT_TRIGGERS, TRIGGER_STAGES, TRIGGER_TYPES, type TriggerAction, type TriggerSettings, type TriggerStage, actionLabel,
  fetchTriggerSettings, firstIncompleteMessage, missingMessageFields, newMessageAction, saveTriggerSettings,
} from './triggers';
import { formatDateFr, inputClass, errorMessage } from './ui';
import {
  type DefaultPrices, EMPTY_DEFAULT_PRICES, applyDefaultPricesToLeads, defaultPriceErrors, fetchDefaultPrices, saveDefaultPrices,
} from './defaultPrices';
import {
  ACCOUNTABLE_ADDRESSES, type AccountingSettings, DEFAULT_ACCOUNTING_SETTINGS, DIRECT_DEBIT_VARIABLES, DOCUMENT_VARIABLES, MESSAGE_KINDS, MESSAGE_LABELS,
  type MessageKind, SENDER_DOMAIN,
  fetchAccountingSettings, isAllowedSender, saveAccountingSettings,
} from './accounting';

const TEMPLATE_HELP: Record<TemplateKey, string> = {
  contract: 'Accompagne le contrat PDF envoyé au passage en « Contrat envoyé ».',
  invitation: 'Envoyé avec le bouton « Envoyer l’invitation à la plateforme » d’un membre actif qui n’a pas encore de compte.',
  welcome: 'Envoyé avec le bouton « Lancer l’onboarding » d’un membre actif. Le voyant « Onboarding » passe alors au vert.',
  questionnaire: 'Accompagne le lien vers le formulaire « nous-rejoindre » : Nouveau lead → « Envoyer le formulaire d’adhésion », ou bouton du même nom dans la fiche d’un lead.',
  mandate: 'Accompagne le mandat SEPA seul (fiche d’un particulier consommateur → « Envoyer le mandat SEPA »). Variable utile : {{reference_mandat}}.',
};

const ORDER: TemplateKey[] = ['contract', 'invitation', 'welcome', 'questionnaire', 'mandate'];

export function SettingsTab({ onRequirementsSaved }: { onRequirementsSaved?: () => void }) {
  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto space-y-3">
        <DefaultPricesSection />
        <FieldRequirements onSaved={onRequirementsSaved} />
        <StageTriggersSection />
        <TodoSettingsSection />
        <AccountingSection />
        <SepaSettingsSection />
        <SavingsEstimateSection />
        <ContractTemplates />
        <MessageTemplates />
      </div>
    </div>
  );
}

const OPEN_KEY = 'crm-settings-open';
function readOpen(): string[] {
  try { return JSON.parse(localStorage.getItem(OPEN_KEY) || '[]'); } catch { return []; }
}

/** Section repliable des Paramètres : fermée par défaut, l'état ouvert est mémorisé dans le navigateur. */
function SettingsSection({ id, icon, title, actions, children }: {
  id: string; icon: React.ReactNode; title: string; actions?: React.ReactNode; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(() => readOpen().includes(id));
  const toggle = () => setOpen(o => {
    const next = !o;
    try {
      const list = readOpen().filter(k => k !== id);
      localStorage.setItem(OPEN_KEY, JSON.stringify(next ? [...list, id] : list));
    } catch { /* stockage indisponible */ }
    return next;
  });
  return (
    <section className={`bg-white border rounded-xl transition-colors ${open ? 'border-neutral-300 shadow-sm' : 'border-neutral-200 hover:border-neutral-300'}`}>
      <div className="flex items-center justify-between gap-2 px-4 py-3">
        <button type="button" onClick={toggle} aria-expanded={open} aria-controls={`settings-${id}`}
          className="flex-1 min-w-0 flex items-center gap-2 text-left">
          <ChevronDown className={`w-4 h-4 text-neutral-400 shrink-0 transition-transform ${open ? '' : '-rotate-90'}`} />
          {icon}
          <h3 className="text-sm font-semibold text-neutral-900 truncate">{title}</h3>
        </button>
        {open && actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
      {open && <div id={`settings-${id}`} className="px-4 pb-4 pt-1 space-y-3 border-t border-neutral-100">{children}</div>}
    </section>
  );
}

const PRICE_ROWS: { entity: 'physique' | 'morale'; label: string }[] = [
  { entity: 'physique', label: 'Particulier (personne physique)' },
  { entity: 'morale', label: 'Professionnel (personne morale)' },
];

/** Tarifs proposés aux nouveaux leads, par type de personne et par sens (crm_settings, clé « default_prices »). */
function DefaultPricesSection() {
  const [saved, setSaved] = useState<DefaultPrices | null>(null);
  const [draft, setDraft] = useState<DefaultPrices | null>(null);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    fetchDefaultPrices(true)
      .catch(err => { toast.error(`Tarifs par défaut : ${errorMessage(err)}`); return EMPTY_DEFAULT_PRICES; })
      .then(v => { setSaved(v); setDraft(v); });
  }, []);

  const dirty = !!draft && JSON.stringify(draft) !== JSON.stringify(saved);
  const errors = draft ? defaultPriceErrors(draft) : {};
  const hasErrors = Object.keys(errors).length > 0;
  const setValue = (entity: 'physique' | 'morale', key: 'consumer' | 'producer', raw: string) =>
    setDraft(d => (d ? { ...d, [entity]: { ...d[entity], [key]: raw === '' ? null : Number(raw) } } : d));

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const clean = await saveDefaultPrices(draft);
      setSaved(clean);
      setDraft(clean);
      toast.success('Tarifs par défaut enregistrés');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const applyToLeads = async () => {
    if (!saved) return;
    setApplying(true);
    try {
      const n = await applyDefaultPricesToLeads(saved);
      toast.success(n === 0 ? 'Tous les leads ont déjà un tarif' : `Tarif par défaut appliqué à ${n} lead${n > 1 ? 's' : ''}`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  return (
    <SettingsSection id="default-prices" icon={<Euro className="w-4 h-4 text-neutral-500" />} title="Tarifs par défaut" actions={<>
      <button onClick={applyToLeads} disabled={applying || dirty || !saved}
        title={dirty ? 'Enregistrez d’abord les tarifs' : 'Remplit le tarif des leads qui n’en ont pas encore'}
        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-neutral-200 text-[11px] text-neutral-600 hover:bg-neutral-50 disabled:opacity-40">
        {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}Appliquer aux leads sans tarif
      </button>
      <button onClick={save} disabled={!dirty || saving || hasErrors}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-[11px] font-semibold text-neutral-900 disabled:opacity-40">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Enregistrer
      </button>
    </>}>
      <p className="text-xs text-neutral-500">
        Prix du contrat proposé à un nouveau lead (formulaire « Nous rejoindre », nouveau lead ou fiche), en c€/kWh HTVA.
        Consommateur : tarif consommation. Producteur : tarif production. Prosumer : prix d’achat = tarif consommation, prix de vente = tarif production.
        Un tarif saisi dans une fiche n’est jamais remplacé ; le prix d’un groupe existant reste prioritaire.
      </p>
      {!draft ? (
        <div className="flex items-center gap-2 text-xs text-neutral-400 py-4"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[11px] text-neutral-500">
                <th className="py-2 pr-3 font-medium" />
                <th className="py-2 px-2 font-medium">Consommation (achat)</th>
                <th className="py-2 px-2 font-medium">Production (vente)</th>
              </tr>
            </thead>
            <tbody>
              {PRICE_ROWS.map(({ entity, label }) => (
                <tr key={entity} className="border-t border-neutral-100">
                  <td className="py-2 pr-3 font-medium text-neutral-800 whitespace-nowrap">{label}</td>
                  {(['consumer', 'producer'] as const).map(k => {
                    const err = errors[`${entity}.${k}`];
                    return (
                      <td key={k} className="py-2 px-2 align-top">
                        <div className="flex items-center gap-1.5">
                          <input type="number" step="any" min="0" value={draft[entity][k] ?? ''} placeholder="—"
                            onChange={e => setValue(entity, k, e.target.value)}
                            className={`${inputClass} w-28 ${err ? 'border-red-300' : 'border-neutral-200'}`} />
                          <span className="text-[11px] text-neutral-400 whitespace-nowrap">c€/kWh</span>
                        </div>
                        {err && <span className="block text-[10px] text-red-600 mt-1">{err}</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10px] text-neutral-400 mt-2">Champ vide : aucun tarif proposé pour ce cas.</p>
        </div>
      )}
    </SettingsSection>
  );
}

function ContractTemplates() {
  const [files, setFiles] = useState<Map<string, string | null> | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const refresh = () => listContractTemplates().then(setFiles).catch(err => toast.error(`Modèles de contrat : ${errorMessage(err)}`));
  useEffect(() => {
    refresh();
  }, []);

  const handleUpload = async (fileName: string, label: string, file: File) => {
    if (file.type !== 'application/pdf') { toast.error('Le modèle doit être un fichier PDF'); return; }
    setUploading(fileName);
    try {
      await uploadContractTemplate(fileName, file);
      toast.success(`Modèle « ${label} » mis à jour`);
      await refresh();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setUploading(null);
    }
  };

  const roles: Role[] = ['consumer', 'producer', 'prosumer'];

  return (
    <SettingsSection id="contract-templates" icon={<FileSignature className="w-4 h-4 text-neutral-500" />} title="Modèles de contrat">
      <p className="text-xs text-neutral-500">
        Un PDF à champs remplissables par type de membre et par type de personne. Le modèle est choisi selon la fiche
        (type et personne physique / morale). Les champs sont reconnus par leur nom, celui des conventions Sun Is Up :
        Nom, Prenom, Denomination, N_BCE, Code_EAN_du_point_dacces, Puissance_crete, Prix_de_lelectricite_partagee…
        Le contrat envoyé reste un PDF modifiable : le membre peut corriger un champ, cocher ses choix et signer.
        Les coordonnées de la Communauté (N° BCE, e-mail) sont écrites dans les modèles et ne sont pas modifiables.
      </p>

      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-neutral-50 text-[11px] text-neutral-500">
              <th className="text-left font-medium px-4 py-2">Type de membre</th>
              <th className="text-left font-medium px-4 py-2">Personne physique</th>
              <th className="text-left font-medium px-4 py-2">Personne morale</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {roles.map(role => (
              <tr key={role}>
                <td className="px-4 py-3 font-medium text-neutral-800 align-top">{ROLE_LABELS[role]}</td>
                {CONTRACT_TEMPLATES.filter(t => t.role === role).map(slot => {
                  const present = files?.has(slot.file) ?? false;
                  const legacy = LEGACY_CONTRACT_TEMPLATE_FILES[role];
                  const legacyUsed = !present && !!files?.has(legacy);
                  return (
                    <td key={slot.file} className="px-4 py-3 align-top">
                      <div className="flex items-start gap-2">
                        {!files ? <Loader2 className="w-4 h-4 mt-0.5 animate-spin text-neutral-300 shrink-0" />
                          : present ? <CheckCircle className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />
                          : legacyUsed ? <CheckCircle className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />
                          : <XCircle className="w-4 h-4 mt-0.5 text-red-400 shrink-0" />}
                        <div className="min-w-0 space-y-2">
                          <p className="text-[11px] text-neutral-500">
                            {present ? `Mis à jour le ${formatDateFr(files?.get(slot.file) ?? null)}`
                              : legacyUsed ? `Ancien modèle ${legacy} utilisé`
                              : 'Aucun modèle déposé'}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {(present || legacyUsed) && (
                              <button
                                onClick={() => openStoredFile('contract-templates', present ? slot.file : legacy).catch(err => toast.error(errorMessage(err)))}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-600 hover:bg-neutral-50"
                              >
                                <Eye className="w-3.5 h-3.5" />Voir
                              </button>
                            )}
                            <label className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-xs text-amber-800 hover:bg-amber-100 cursor-pointer">
                              {uploading === slot.file ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                              {present ? 'Remplacer' : 'Déposer'}
                              <input type="file" accept="application/pdf" className="hidden"
                                onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(slot.file, `${ROLE_LABELS[slot.role]} · ${ENTITY_LABELS[slot.entity].toLowerCase()}`, f); e.target.value = ''; }} />
                            </label>
                          </div>
                        </div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
        {EXTRA_CONTRACT_TEMPLATES.map(t => {
          const present = files?.has(t.file) ?? false;
          return (
            <div key={t.file} className="flex items-start gap-3 px-4 py-3">
              {!files ? <Loader2 className="w-4 h-4 mt-0.5 animate-spin text-neutral-300 shrink-0" />
                : present ? <CheckCircle className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />
                : <XCircle className="w-4 h-4 mt-0.5 text-red-400 shrink-0" />}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-neutral-800">{t.label} <span className="font-mono text-[10px] font-normal text-neutral-400">{t.file}</span></p>
                <p className="text-[11px] text-neutral-500">
                  {present ? `Mis à jour le ${formatDateFr(files?.get(t.file) ?? null)}` : 'Aucun modèle déposé'} · {t.help}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                {present && (
                  <button onClick={() => openStoredFile('contract-templates', t.file).catch(err => toast.error(errorMessage(err)))}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-600 hover:bg-neutral-50">
                    <Eye className="w-3.5 h-3.5" />Voir
                  </button>
                )}
                <label className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-xs text-amber-800 hover:bg-amber-100 cursor-pointer">
                  {uploading === t.file ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  {present ? 'Remplacer' : 'Déposer'}
                  <input type="file" accept="application/pdf" className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(t.file, t.label, f); e.target.value = ''; }} />
                </label>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-neutral-500">
        Champs du mandat remplis et verrouillés : Mandat_Reference, Mandat_Debiteur_Nom (titulaire du compte), Mandat_Debiteur_Adresse (domicile),
        Mandat_IBAN, Mandat_BIC, et Mandat_Creancier_Nom, Mandat_Creancier_Adresse, Mandat_Creancier_Identifiant s’ils existent.
        Restent modifiables : Mandat_Lieu, Mandat_Date et la signature.
      </p>
    </SettingsSection>
  );
}

function MessageTemplates() {
  const [templates, setTemplates] = useState<MessageTemplate[] | null>(null);
  const [selected, setSelected] = useState<TemplateKey>('welcome');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    fetchTemplates().then(setTemplates).catch(err => toast.error(`Messages : ${errorMessage(err)}`));
  }, []);

  const current = templates?.find(t => t.key === selected) ?? null;
  useEffect(() => {
    setSubject(current?.subject ?? '');
    setBody(current?.body ?? '');
  }, [current]);

  const dirty = !!current && (subject !== current.subject || body !== current.body);

  const select = (key: TemplateKey) => {
    if (dirty && !window.confirm('Les modifications non enregistrées seront perdues. Continuer ?')) return;
    setSelected(key);
  };

  const insertVariable = (v: string) => {
    const el = bodyRef.current;
    if (!el) { setBody(b => b + v); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + v + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + v.length, start + v.length); });
  };

  const handleSave = async () => {
    if (!subject.trim()) { toast.error("L'objet est obligatoire"); return; }
    if (!body.trim()) { toast.error('Le message est obligatoire'); return; }
    setSaving(true);
    try {
      const saved = await saveTemplate(selected, subject.trim(), body);
      setTemplates(prev => (prev ?? []).map(t => (t.key === saved.key ? saved : t)));
      toast.success('Message enregistré');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsSection id="messages" icon={<Mail className="w-4 h-4 text-neutral-500" />} title="Messages envoyés par le CRM">
      {!templates ? (
        <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-neutral-300" /></div>
      ) : templates.length === 0 ? (
        <p className="text-xs text-red-600">Aucun modèle trouvé : la migration Supabase du CRM n’a pas encore été exécutée.</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-[220px_minmax(0,1fr)]">
          <div className="space-y-1">
            {ORDER.map(key => {
              const t = templates.find(x => x.key === key);
              if (!t) return null;
              return (
                <button key={key} onClick={() => select(key)}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-xs ${selected === key ? 'border-[#F8BD3D] bg-amber-50 text-neutral-900 font-semibold' : 'border-transparent text-neutral-600 hover:bg-neutral-50'}`}>
                  {t.label}
                  <span className="block text-[10px] font-normal text-neutral-400">Modifié le {formatDateFr(t.updated_at)}</span>
                </button>
              );
            })}
          </div>
          <div className="space-y-3 bg-white border border-neutral-200 rounded-xl p-4">
            <p className="text-xs text-neutral-500">{TEMPLATE_HELP[selected]}</p>
            <label className="block">
              <span className="block text-[11px] font-medium text-neutral-600 mb-1">Objet</span>
              <input value={subject} onChange={e => setSubject(e.target.value)} className={`${inputClass} border-neutral-200`} />
            </label>
            <label className="block">
              <span className="block text-[11px] font-medium text-neutral-600 mb-1">Message</span>
              <textarea ref={bodyRef} rows={14} value={body} onChange={e => setBody(e.target.value)} className={`${inputClass} border-neutral-200 font-mono text-[13px] leading-relaxed`} />
            </label>
            <div>
              <p className="text-[11px] font-medium text-neutral-600 mb-1">Insérer une variable</p>
              <div className="flex flex-wrap gap-1">
                {TEMPLATE_VARIABLES.map(v => (
                  <button key={v.key} type="button" onClick={() => insertVariable(v.key)} title={v.label}
                    className="px-2 py-0.5 rounded-md border border-neutral-200 text-[11px] font-mono text-neutral-600 hover:bg-neutral-50">
                    {v.key}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-neutral-400 mt-1">Les retours à la ligne sont conservés. Le gras s’écrit &lt;strong&gt;texte&lt;/strong&gt;.</p>
            </div>
            <div className="flex justify-end">
              <button onClick={handleSave} disabled={saving || !dirty}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-sm font-semibold text-neutral-900 disabled:opacity-40">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </SettingsSection>
  );
}

const STAGE_LABEL: Record<RequirementStage, string> = {
  ...Object.fromEntries(STAGES.map(st => [st.key, st.label])) as Record<string, string>,
  never: 'Facultatif',
} as Record<RequirementStage, string>;

/** Étape à partir de laquelle chaque champ est exigé. Les champs essentiels du formulaire admin restent exigés. */
function FieldRequirements({ onSaved }: { onSaved?: () => void }) {
  const [saved, setSaved] = useState<RequirementOverrides | null>(null);
  const [draft, setDraft] = useState<RequirementOverrides>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchFieldRequirements()
      .then(v => { setSaved(v); setDraft(v); })
      .catch(err => { setSaved({}); toast.error(`Champs obligatoires : ${errorMessage(err)} (le script SQL 20261023180000 a-t-il été exécuté ?)`); });
  }, []);

  const valueOf = (id: string) => requirementStage(REQUIREMENT_RULES.find(r => r.id === id)!, draft);
  const dirty = saved !== null && JSON.stringify(compact(draft)) !== JSON.stringify(compact(saved));
  const sections = Array.from(new Set(REQUIREMENT_RULES.map(r => r.section)));

  const save = async () => {
    setSaving(true);
    try {
      const value = compact(draft);
      await saveFieldRequirements(value);
      setRequirementOverrides(value);
      setSaved(value);
      setDraft(value);
      onSaved?.();
      toast.success('Champs obligatoires enregistrés');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsSection id="requirements" icon={<ListChecks className="w-4 h-4 text-neutral-500" />} title="Champs obligatoires" actions={<>
        <div className="flex items-center gap-2">
          <button onClick={() => setDraft({})} disabled={saved === null}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-neutral-200 text-[11px] text-neutral-600 hover:bg-neutral-50 disabled:opacity-40">
            <RotateCcw className="w-3.5 h-3.5" />Valeurs par défaut
          </button>
          <button onClick={save} disabled={!dirty || saving}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-[11px] font-semibold text-neutral-900 disabled:opacity-40">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Enregistrer
          </button>
        </div>
      </>}>
      <p className="text-xs text-neutral-500">
        Pour chaque champ, l’étape à partir de laquelle il est exigé. Un champ exigé à une étape l’est aussi pour les suivantes.
        Les champs exigés au stade « Leads » sont demandés à la création d’un lead et lors de l’envoi du formulaire d’adhésion.
        Les champs marqués d’un cadenas sont fixés : ils servent au contrat, à l’affichage, ou sont obligatoires dans le formulaire participant de la page admin.
      </p>
      {saved === null ? (
        <div className="flex items-center gap-2 text-xs text-neutral-400 py-4"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-100 text-[10px] font-semibold text-neutral-500 uppercase tracking-wide">
                <th className="text-left px-4 py-2">Champ</th>
                <th className="text-left px-3 py-2">S’applique à</th>
                <th className="text-left px-3 py-2 w-64">Exigé à partir de</th>
              </tr>
            </thead>
            <tbody>
              {sections.map(section => (
                <FieldSection key={section} section={section}>
                  {REQUIREMENT_RULES.filter(r => r.section === section).map(rule => {
                    const locked = rule.allowed.length === 1;
                    const value = valueOf(rule.id);
                    const changed = value !== rule.defaultStage;
                    return (
                      <tr key={rule.id} className="border-b border-neutral-50">
                        <td className="px-4 py-2 text-neutral-800">{rule.label}</td>
                        <td className="px-3 py-2 text-neutral-500">{rule.appliesLabel ?? 'Tous'}</td>
                        <td className="px-3 py-2">
                          {locked ? (
                            <span className="inline-flex items-center gap-1.5 text-neutral-500" title={rule.lockReason}>
                              <Lock className="w-3 h-3" />{STAGE_LABEL[value]}
                            </span>
                          ) : (
                            <select
                              value={value}
                              title={rule.lockReason}
                              onChange={e => setDraft(d => ({ ...d, [rule.id]: e.target.value as RequirementStage }))}
                              className={`${inputClass} py-1 text-xs ${changed ? 'border-amber-300 bg-amber-50/50' : 'border-neutral-200'}`}
                            >
                              {rule.allowed.map(st => <option key={st} value={st}>{STAGE_LABEL[st]}{st === rule.defaultStage ? ' (défaut)' : ''}</option>)}
                            </select>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </FieldSection>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SettingsSection>
  );
}

function FieldSection({ section, children }: { section: string; children: React.ReactNode }) {
  return (
    <>
      <tr className="bg-neutral-50/60"><td colSpan={3} className="px-4 pt-3 pb-1 text-[10px] font-semibold text-neutral-400 uppercase tracking-wide">{section}</td></tr>
      {children}
    </>
  );
}

/** Ne garde que les réglages différents des valeurs par défaut. */
function compact(overrides: RequirementOverrides): RequirementOverrides {
  const out: RequirementOverrides = {};
  for (const rule of REQUIREMENT_RULES) {
    const v = overrides[rule.id];
    if (v && v !== rule.defaultStage && rule.allowed.includes(v)) out[rule.id] = v;
  }
  return out;
}

/** Todolist : opérations suivies, points de départ et récap mensuel. */
function TodoSettingsSection() {
  const [operations, setOperations] = useState<SharingOperation[]>([]);
  const [saved, setSaved] = useState<TodoSettings | null>(null);
  const [draft, setDraft] = useState<TodoSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    Promise.all([fetchOperations(), fetchTodoSettings()])
      .then(([ops, settings]) => { setOperations(ops); setSaved(settings); setDraft(settings); })
      .catch(err => toast.error(`Todolist : ${errorMessage(err)} (le script SQL 20261023200000 a-t-il été exécuté ?)`));
  }, []);

  const opValue = (id: string): TodoOperationSettings =>
    draft?.operations[id] ?? { tracked: true, invoicedThrough: null, reimbursedThrough: null };
  const setOp = (id: string, patch: Partial<TodoOperationSettings>) =>
    setDraft(d => (d ? { ...d, operations: { ...d.operations, [id]: { ...opValue(id), ...patch } } } : d));
  const dirty = !!draft && JSON.stringify(draft) !== JSON.stringify(saved);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await saveTodoSettings(draft);
      setSaved(draft);
      toast.success('Réglages de la todolist enregistrés');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await sendDigestNow();
      toast.success(`Récap envoyé à ${res.to}`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const monthInput = 'px-2 py-1 text-xs border border-neutral-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#F8BD3D]/50';

  return (
    <SettingsSection id="todo" icon={<CalendarClock className="w-4 h-4 text-neutral-500" />} title="Todolist et récap mensuel" actions={<>
        <div className="flex items-center gap-2">
          <button onClick={sendNow} disabled={sending}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-neutral-200 text-[11px] text-neutral-600 hover:bg-neutral-50 disabled:opacity-40">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}Envoyer le récap maintenant
          </button>
          <button onClick={save} disabled={!dirty || saving}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-[11px] font-semibold text-neutral-900 disabled:opacity-40">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Enregistrer
          </button>
        </div>
      </>}>
      <p className="text-xs text-neutral-500">
        Le récap de la todolist part automatiquement le 1er de chaque mois vers info@sunisup.be. Les points de départ indiquent ce qui a déjà été fait hors CRM :
        les périodes antérieures n’apparaissent pas. Ensuite, chaque ligne cochée dans la todolist est mémorisée.
      </p>
      {!draft ? (
        <div className="flex items-center gap-2 text-xs text-neutral-400 py-4"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-100 text-[10px] font-semibold text-neutral-500 uppercase tracking-wide">
                <th className="text-left px-4 py-2">Opération</th>
                <th className="text-left px-3 py-2">Suivie</th>
                <th className="text-left px-3 py-2">Producteurs remboursés jusqu’à</th>
                <th className="text-left px-3 py-2">Consommateurs facturés jusqu’à</th>
              </tr>
            </thead>
            <tbody>
              {operations.map(o => {
                const v = opValue(o.id);
                return (
                  <tr key={o.id} className="border-b border-neutral-50">
                    <td className="px-4 py-2 text-neutral-800">{o.name}</td>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={v.tracked} onChange={e => setOp(o.id, { tracked: e.target.checked })} aria-label={`Suivre ${o.name}`} />
                    </td>
                    <td className="px-3 py-2">
                      <input type="month" value={v.reimbursedThrough ?? ''} onChange={e => setOp(o.id, { reimbursedThrough: e.target.value || null })} className={monthInput} disabled={!v.tracked} />
                    </td>
                    <td className="px-3 py-2">
                      <input type="month" value={v.invoicedThrough ?? ''} onChange={e => setOp(o.id, { invoicedThrough: e.target.value || null })} className={monthInput} disabled={!v.tracked} />
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td className="px-4 py-2 text-neutral-800" colSpan={2}>Rapports semestriels suivis à partir de</td>
                <td className="px-3 py-2" colSpan={2}>
                  <input type="month" value={draft.reportsFrom} onChange={e => setDraft(d => (d ? { ...d, reportsFrom: e.target.value || d.reportsFrom } : d))} className={monthInput} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </SettingsSection>
  );
}

/** Comptabilité : adresse du compte Accountable, destinations par défaut des décomptes, messages joints aux factures et décomptes. */
function AccountingSection() {
  const [saved, setSaved] = useState<AccountingSettings | null>(null);
  const [draft, setDraft] = useState<AccountingSettings | null>(null);
  const [kind, setKind] = useState<MessageKind>('invoice');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAccountingSettings()
      .catch(err => { toast.error(`Comptabilité : ${errorMessage(err)}`); return DEFAULT_ACCOUNTING_SETTINGS; })
      .then(v => { setSaved(v); setDraft(v); });
  }, []);

  const dirty = !!draft && JSON.stringify(draft) !== JSON.stringify(saved);
  const senderError = draft && !isAllowedSender(draft.accountableSender) ? `L’adresse doit se terminer par @${SENDER_DOMAIN}` : null;
  const setMessage = (patch: Partial<AccountingSettings['messages'][MessageKind]>) =>
    setDraft(d => (d ? { ...d, messages: { ...d.messages, [kind]: { ...d.messages[kind], ...patch } } } : d));

  const save = async () => {
    if (!draft) return;
    if (senderError) { toast.error(senderError); return; }
    if (MESSAGE_KINDS.some(k => !draft.messages[k].subject.trim() || !draft.messages[k].body.trim())) {
      toast.error('Chaque message a besoin d’un objet et d’un texte'); return;
    }
    setSaving(true);
    try {
      await saveAccountingSettings(draft);
      const clean = { ...draft, accountableSender: draft.accountableSender.trim().toLowerCase() };
      setSaved(clean);
      setDraft(clean);
      toast.success('Réglages de comptabilité enregistrés');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsSection id="accounting" icon={<BookOpenCheck className="w-4 h-4 text-neutral-500" />} title="Comptabilité : envoi des factures et décomptes" actions={<>
        <button onClick={save} disabled={!dirty || saving}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-[11px] font-semibold text-neutral-900 disabled:opacity-40">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Enregistrer
        </button>
      </>}>
      <p className="text-xs text-neutral-500">
        Facturation des consommateurs (todolist) : <b>toutes les factures</b> partent vers {ACCOUNTABLE_ADDRESSES.revenue}, un décompte (montant négatif)
        vers {ACCOUNTABLE_ADDRESSES.expense}. Le membre reçoit un e-mail si sa ligne est cochée à l’envoi : par défaut, les nouveaux membres (mois d’entrée = période facturée, modèles « Première facture » / « Premier décompte ») ;
        domiciliation : toujours (message « Facture – domiciliation »). Modèle au choix et texte modifiable pour chaque membre au moment de l’envoi. Décomptes des producteurs : Accountable (sociétés) et/ou e-mail.
        Accountable rattache le document au compte dont l’adresse l’a envoyé.
      </p>
      {!draft ? (
        <div className="flex items-center gap-2 text-xs text-neutral-400 py-4"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="block text-[11px] font-medium text-neutral-600 mb-1">Adresse du compte Accountable (expéditeur)</span>
              <input value={draft.accountableSender} onChange={e => setDraft(d => (d ? { ...d, accountableSender: e.target.value } : d))}
                className={`${inputClass} ${senderError ? 'border-red-300' : 'border-neutral-200'}`} />
              <span className={`block text-[10px] mt-1 ${senderError ? 'text-red-600' : 'text-neutral-400'}`}>
                {senderError ?? `Doit être l’adresse de connexion du compte Accountable de Sun Is Up, sur le domaine ${SENDER_DOMAIN}.`}
              </span>
            </label>
            <div>
              <span className="block text-[11px] font-medium text-neutral-600 mb-1">Décomptes des sociétés</span>
              <label className="flex items-center gap-2 text-xs text-neutral-700 py-1">
                <input type="checkbox" checked={draft.sendToAccountable} onChange={e => setDraft(d => (d ? { ...d, sendToAccountable: e.target.checked } : d))} />
                Accountable coché par défaut
              </label>
              <span className="block text-[10px] text-neutral-400">E-mail au membre : choisi ligne par ligne à l’envoi (par défaut : nouveaux membres).</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-1">
              {MESSAGE_KINDS.map(k => (
                <button key={k} onClick={() => setKind(k)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${kind === k ? 'bg-neutral-900 text-white' : 'text-neutral-500 hover:bg-neutral-100'}`}>
                  {MESSAGE_LABELS[k]}
                </button>
              ))}
            </div>
            <input value={draft.messages[kind].subject} onChange={e => setMessage({ subject: e.target.value })} placeholder="Objet" className={`${inputClass} border-neutral-200`} />
            <textarea rows={7} value={draft.messages[kind].body} onChange={e => setMessage({ body: e.target.value })}
              className={`${inputClass} border-neutral-200 font-mono text-[13px] leading-relaxed`} />
            <p className="text-[10px] text-neutral-400">
              Variables : {[...DOCUMENT_VARIABLES, ...(kind === 'direct_debit' ? DIRECT_DEBIT_VARIABLES : [])].map(v => `${v.key} (${v.label.toLowerCase()})`).join(' · ')}.
              {kind === 'direct_debit'
                ? ' Pré-notification : la facture part au moins 5 jours ouvrables avant le prélèvement, avec son montant et sa date.'
                : ' Le texte reste modifiable pour chaque membre au moment de l’envoi (« Aperçu du mail »).'}
            </p>
          </div>
        </div>
      )}
    </SettingsSection>
  );
}

/** Déclencheurs : statut d'attente et actions proposées au passage à chaque étape. */
function StageTriggersSection() {
  const [saved, setSaved] = useState<TriggerSettings | null>(null);
  const [draft, setDraft] = useState<TriggerSettings | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const textRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});

  useEffect(() => {
    fetchTriggerSettings(true)
      .catch(err => { toast.error(`Déclencheurs : ${errorMessage(err)}`); return DEFAULT_TRIGGERS; })
      .then(v => { setSaved(v); setDraft(v); });
  }, []);

  const dirty = !!draft && JSON.stringify(draft) !== JSON.stringify(saved);
  const setStage = (stage: TriggerStage, fn: (st: TriggerSettings[TriggerStage]) => TriggerSettings[TriggerStage]) =>
    setDraft(d => (d ? { ...d, [stage]: fn(d[stage]) } : d));
  const setAction = (stage: TriggerStage, id: string, patch: Partial<TriggerAction>) =>
    setStage(stage, st => ({ ...st, actions: st.actions.map(a => (a.id === id ? { ...a, ...patch } : a)) }));

  const insertVariable = (stage: TriggerStage, a: TriggerAction, v: string) => {
    const el = textRefs.current[a.id];
    const body = a.body ?? '';
    const start = el?.selectionStart ?? body.length;
    const end = el?.selectionEnd ?? body.length;
    setAction(stage, a.id, { body: body.slice(0, start) + v + body.slice(end) });
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(start + v.length, start + v.length); } });
  };

  const [showMissing, setShowMissing] = useState(false);
  const save = async () => {
    if (!draft) return;
    // Message incomplet : il est ouvert et ses champs vides sont signalés.
    const incomplete = firstIncompleteMessage(draft);
    if (incomplete) { setShowMissing(true); setOpen(incomplete.action.id); }
    setSaving(true);
    try {
      await saveTriggerSettings(draft);
      setShowMissing(false);
      setSaved(draft);
      toast.success('Déclencheurs enregistrés');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const stageInfo = (stage: TriggerStage) => STAGES.find(st => st.key === stage)!;

  return (
    <SettingsSection id="triggers" icon={<Zap className="w-4 h-4 text-neutral-500" />} title="Déclencheurs des changements d’étape" actions={<>
        <div className="flex items-center gap-2">
          <button onClick={() => setDraft(DEFAULT_TRIGGERS)} disabled={!draft}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-neutral-200 text-[11px] text-neutral-600 hover:bg-neutral-50 disabled:opacity-40">
            <RotateCcw className="w-3.5 h-3.5" />Valeurs par défaut
          </button>
          <button onClick={save} disabled={!dirty || saving}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-[11px] font-semibold text-neutral-900 disabled:opacity-40">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Enregistrer
          </button>
        </div>
      </>}>
      <p className="text-xs text-neutral-500">
        Ce qui se passe quand un contact arrive à une étape. Les actions activées apparaissent cochées dans la fenêtre de changement d’étape :
        elles peuvent y être décochées pour un contact. Seules les actions de l’étape d’arrivée s’exécutent (un passage direct de Leads à Membre actif
        n’envoie pas le contrat). Un retour en arrière ne déclenche rien.
      </p>
      {!draft ? (
        <div className="flex items-center gap-2 text-xs text-neutral-400 py-4"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</div>
      ) : (
        <div className="space-y-3">
          {TRIGGER_STAGES.map(stage => {
            const st = draft[stage];
            const info = stageInfo(stage);
            return (
              <div key={stage} className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 bg-neutral-50/70 border-b border-neutral-100">
                  <span className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
                    <span className={`w-2 h-2 rounded-full ${info.dot}`} />Passage à « {info.label} »
                  </span>
                  <label className="flex items-center gap-2 text-[11px] text-neutral-600">
                    Statut d’attente posé
                    <select value={st.waiting ?? ''} onChange={e => setStage(stage, s0 => ({ ...s0, waiting: (e.target.value || null) as WaitingOn | null }))}
                      className="px-2 py-1 text-xs border border-neutral-200 rounded-lg bg-white">
                      <option value="client">{WAITING_LABELS.client}</option>
                      <option value="sunisup">{WAITING_LABELS.sunisup}</option>
                      <option value="">Aucun</option>
                    </select>
                  </label>
                </div>
                <ul className="divide-y divide-neutral-100">
                  {stage === 'active' && (
                    <li className="flex items-start gap-3 px-4 py-2.5">
                      <Lock className="w-3.5 h-3.5 mt-0.5 text-neutral-400" />
                      <span className="text-xs text-neutral-500">Toujours : création des fiches participants (une par EAN), rangées dans le groupe prévu.</span>
                    </li>
                  )}
                  {st.actions.map(a => {
                    const isMessage = a.type === 'send_message';
                    const expanded = open === a.id;
                    const missing = showMissing ? missingMessageFields(a) : [];
                    const fieldClass = (k: 'label' | 'subject' | 'body') => `${inputClass} ${missing.includes(k) ? 'border-red-300 bg-red-50/40' : 'border-neutral-200'}`;
                    const fieldError = (k: 'label' | 'subject' | 'body') => missing.includes(k) && <span className="block text-[11px] text-red-600 mt-1">Obligatoire</span>;
                    return (
                      <li key={a.id} className="px-4 py-2.5">
                        <div className="flex items-start gap-3">
                          <input type="checkbox" className="mt-0.5" checked={a.enabled} onChange={e => setAction(stage, a.id, { enabled: e.target.checked })}
                            aria-label={`Activer ${actionLabel(a)}`} />
                          <div className="min-w-0 flex-1">
                            <p className={`text-xs font-medium ${a.enabled ? 'text-neutral-900' : 'text-neutral-500'}`}>
                              {isMessage ? <>Envoyer le message « {actionLabel(a)} »</> : actionLabel(a)}
                            </p>
                            <p className={`text-[11px] truncate ${missing.length ? 'text-red-600' : 'text-neutral-500'}`}>
                              {isMessage && missing.length ? 'À compléter avant d’enregistrer' : isMessage ? `Objet : ${a.subject || '—'}` : TRIGGER_TYPES[a.type as Exclude<TriggerAction['type'], 'send_message'>].description}
                            </p>
                          </div>
                          {isMessage && (
                            <div className="flex items-center gap-1">
                              <button onClick={() => setOpen(expanded ? null : a.id)}
                                className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-neutral-600 hover:bg-neutral-100">
                                {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}Modifier
                              </button>
                              <button onClick={() => { if (window.confirm(`Supprimer le message « ${actionLabel(a)} » ?`)) setStage(stage, s0 => ({ ...s0, actions: s0.actions.filter(x => x.id !== a.id) })); }}
                                className="p-1 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50" aria-label={`Supprimer ${actionLabel(a)}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                        {isMessage && expanded && (
                          <div className="mt-3 ml-7 space-y-2">
                            <label className="block">
                              <span className="block text-[11px] font-medium text-neutral-600 mb-1">Nom du déclencheur <span className="text-red-500">*</span></span>
                              <input value={a.label ?? ''} onChange={e => setAction(stage, a.id, { label: e.target.value })} className={fieldClass('label')} />
                              {fieldError('label')}
                            </label>
                            <label className="block">
                              <span className="block text-[11px] font-medium text-neutral-600 mb-1">Objet de l’e-mail <span className="text-red-500">*</span></span>
                              <input value={a.subject ?? ''} onChange={e => setAction(stage, a.id, { subject: e.target.value })} placeholder="Ex. Votre dossier avance – Sun Is Up" className={fieldClass('subject')} />
                              {fieldError('subject')}
                            </label>
                            <label className="block">
                              <span className="block text-[11px] font-medium text-neutral-600 mb-1">Message <span className="text-red-500">*</span></span>
                              <textarea ref={el => { textRefs.current[a.id] = el; }} rows={7} value={a.body ?? ''} onChange={e => setAction(stage, a.id, { body: e.target.value })}
                                className={`${fieldClass('body')} font-mono text-[13px] leading-relaxed`} />
                              {fieldError('body')}
                            </label>
                            <div className="flex flex-wrap gap-1">
                              {TEMPLATE_VARIABLES.map(v => (
                                <button key={v.key} type="button" onClick={() => insertVariable(stage, a, v.key)} title={v.label}
                                  className="px-2 py-0.5 rounded-md border border-neutral-200 text-[11px] font-mono text-neutral-600 hover:bg-neutral-50">
                                  {v.key}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
                <div className="px-4 py-2 border-t border-neutral-100">
                  <button onClick={() => { const a = newMessageAction(); setStage(stage, s0 => ({ ...s0, actions: [...s0.actions, a] })); setOpen(a.id); }}
                    className="flex items-center gap-1 text-[11px] font-medium text-neutral-600 hover:text-neutral-900">
                    <Plus className="w-3.5 h-3.5" />Ajouter un message
                  </button>
                </div>
              </div>
            );
          })}
          {/* Barre d'enregistrement toujours visible en bas de l'écran dès qu'il y a une modification. */}
          {dirty && (
            <div className="sticky bottom-0 z-10 -mx-4 px-4 py-3 bg-white/95 backdrop-blur border-t border-amber-200 flex flex-wrap items-center justify-end gap-2 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
              <span className="mr-auto text-xs font-medium text-amber-800">Modifications non enregistrées</span>
              <button onClick={() => { setDraft(saved); setShowMissing(false); }} disabled={saving}
                className="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-600 hover:bg-neutral-50 disabled:opacity-40">
                Annuler les modifications
              </button>
              <button onClick={save} disabled={saving}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-xs font-semibold text-neutral-900 disabled:opacity-40">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Enregistrer les déclencheurs
              </button>
            </div>
          )}
        </div>
      )}
    </SettingsSection>
  );
}

/** Domiciliation SEPA : créancier, format du fichier XML, plafonds BNP et délais (crm_settings, clé « sepa »). */
function SepaSettingsSection() {
  const [saved, setSaved] = useState<SepaSettings | null>(null);
  const [draft, setDraft] = useState<SepaSettings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSepaSettings(true)
      .catch(err => { toast.error(`Domiciliation SEPA : ${errorMessage(err)}`); return DEFAULT_SEPA_SETTINGS; })
      .then(v => { setSaved(v); setDraft(v); });
  }, []);

  const dirty = !!draft && JSON.stringify(draft) !== JSON.stringify(saved);
  const errors = draft ? sepaSettingsErrors(draft) : {};
  const set = <K extends keyof SepaSettings>(key: K, value: SepaSettings[K]) => setDraft(d => (d ? { ...d, [key]: value } : d));

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const clean = await saveSepaSettings(draft);
      setSaved(clean);
      setDraft(clean);
      toast.success('Réglages de la domiciliation enregistrés');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const text = (key: keyof SepaSettings, label: string, opts: { mono?: boolean; full?: boolean } = {}) => (
    <label className={`block ${opts.full ? 'md:col-span-2' : ''}`}>
      <span className="block text-[11px] font-medium text-neutral-600 mb-1">{label}</span>
      <input value={String(draft?.[key] ?? '')} onChange={e => set(key, e.target.value as never)}
        className={`${inputClass} ${opts.mono ? 'font-mono text-[13px]' : ''} ${errors[key] ? 'border-red-300' : 'border-neutral-200'}`} />
      {errors[key] && <span className="block text-[10px] text-red-600 mt-1">{errors[key]}</span>}
    </label>
  );
  const number = (key: keyof SepaSettings, label: string, hint: string) => (
    <label className="block">
      <span className="block text-[11px] font-medium text-neutral-600 mb-1">{label}</span>
      <input type="number" step="any" value={String(draft?.[key] ?? '')} onChange={e => set(key, Number(e.target.value) as never)}
        className={`${inputClass} ${errors[key] ? 'border-red-300' : 'border-neutral-200'}`} />
      <span className={`block text-[10px] mt-1 ${errors[key] ? 'text-red-600' : 'text-neutral-400'}`}>{errors[key] ?? hint}</span>
    </label>
  );

  return (
    <SettingsSection id="sepa" icon={<Landmark className="w-4 h-4 text-neutral-500" />} title="Domiciliation SEPA" actions={<>
        <button onClick={save} disabled={!dirty || saving || Object.keys(errors).length > 0}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-[11px] font-semibold text-neutral-900 disabled:opacity-40">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Enregistrer
        </button>
      </>}>
      <p className="text-xs text-neutral-500">
        Prélèvement des factures des particuliers consommateurs (schéma CORE), via le contrat BNP Paribas Fortis.
        Le créancier figure sur les mandats, les factures et le fichier XML importé dans Easy Banking Business.
      </p>
      {!draft ? (
        <div className="flex items-center gap-2 text-xs text-neutral-400 py-4"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            {text('creditorName', 'Créancier')}
            {text('creditorAddress', 'Adresse du créancier')}
            {text('creditorCompanyNumber', 'N° BCE')}
            {text('creditorId', 'Identifiant créancier SEPA', { mono: true })}
            {text('creditorIban', 'IBAN du créancier', { mono: true })}
            {text('creditorBic', 'BIC', { mono: true })}
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <label className="block">
              <span className="block text-[11px] font-medium text-neutral-600 mb-1">Format du fichier XML</span>
              <select value={draft.format} onChange={e => set('format', e.target.value as SepaFormat)} className={`${inputClass} border-neutral-200`}>
                {SEPA_FORMATS.map(f => <option key={f} value={f}>{f}{f === 'pain.008.001.08' ? ' (standard Febelfin actuel)' : ''}</option>)}
              </select>
              <span className="block text-[10px] text-neutral-400 mt-1">Choisir 001.02 seulement si la banque refuse le 001.08.</span>
            </label>
            {number('maxAmount', 'Montant maximum d’un prélèvement (€)', 'Annexe technique BNP')}
            {number('maxMonthlyTotal', 'Total maximum par mois (€)', 'Selon la date de prélèvement')}
            {number('minBusinessDays', 'Délai minimum (jours ouvrables)', 'Entre l’envoi de la facture et le prélèvement')}
            {number('maxCalendarDays', 'Délai maximum (jours)', 'Après la date du jour')}
            {number('cutoffHour', 'Heure limite d’import (h)', 'La veille ouvrée du prélèvement')}
          </div>
        </div>
      )}
    </SettingsSection>
  );
}

/** Estimation des économies d'un particulier : calcul et phrase ajoutée à l'e-mail du contrat. */
function SavingsEstimateSection() {
  const [saved, setSaved] = useState<SavingsEstimateSettings | null>(null);
  const [draft, setDraft] = useState<SavingsEstimateSettings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSavingsSettings(true)
      .catch(err => { toast.error(`Estimation des économies : ${errorMessage(err)}`); return DEFAULT_SAVINGS_SETTINGS; })
      .then(v => { setSaved(v); setDraft(v); });
  }, []);

  const dirty = !!draft && JSON.stringify(draft) !== JSON.stringify(saved);
  const errors = draft ? savingsSettingsErrors(draft) : {};
  const set = <K extends keyof SavingsEstimateSettings>(key: K, value: SavingsEstimateSettings[K]) => setDraft(d => (d ? { ...d, [key]: value } : d));

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await saveSavingsSettings(draft);
      setSaved(draft);
      toast.success('Réglages de l’estimation enregistrés');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  /** Exemples : maison et appartement sans montant déclaré, puis une facture connue. */
  const examples = draft ? [
    { label: 'Maison, 4 000 kWh, montant inconnu', input: { annual_consumption_kwh: 4000, annual_cost_eur: null } },
    { label: 'Appartement, 1 500 kWh, montant inconnu', input: { annual_consumption_kwh: 1500, annual_cost_eur: null } },
    { label: '3 000 kWh, 1 100 € payés par an', input: { annual_consumption_kwh: 3000, annual_cost_eur: 1100 } },
  ].map(x => ({ ...x, estimate: computeSavings(x.input, draft) })) : [];

  const field = (key: 'defaultPricePerKwh' | 'roundTo', label: string, hint: string, step: string) => (
    <label className="block">
      <span className="block text-[11px] font-medium text-neutral-600 mb-1">{label}</span>
      <input type="number" step={step} value={String(draft?.[key] ?? '')} onChange={e => set(key, Number(e.target.value))}
        className={`${inputClass} ${errors[key] ? 'border-red-300' : 'border-neutral-200'}`} />
      <span className={`block text-[10px] mt-1 ${errors[key] ? 'text-red-600' : 'text-neutral-400'}`}>{errors[key] ?? hint}</span>
    </label>
  );

  return (
    <SettingsSection id="savings" icon={<Calculator className="w-4 h-4 text-neutral-500" />} title="Estimation des économies (particuliers)" actions={<>
        <button onClick={save} disabled={!dirty || saving || Object.keys(errors).length > 0}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-[11px] font-semibold text-neutral-900 disabled:opacity-40">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Enregistrer
        </button>
      </>}>
      <p className="text-xs text-neutral-500">
        Un particulier consommateur ou prosumer peut demander une estimation dans le formulaire d’adhésion (consommation annuelle, montant payé s’il le connaît).
        Calcul : prix par kWh = montant payé ÷ consommation, sinon prix par défaut ; économie = consommation × prix × taux, arrondie.
        Une fois activée, l’estimation est ajoutée à l’e-mail du contrat.
      </p>
      {!draft ? (
        <div className="flex items-center gap-2 text-xs text-neutral-400 py-4"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-4">
          <label className="flex items-center gap-2 text-xs text-neutral-800 cursor-pointer">
            <input type="checkbox" checked={draft.enabled} onChange={e => set('enabled', e.target.checked)} />
            Ajouter l’estimation à l’e-mail du contrat
          </label>
          <div className="grid gap-3 md:grid-cols-3">
            {field('defaultPricePerKwh', 'Prix par défaut (€/kWh TVAC)', 'Utilisé quand le montant payé n’est pas indiqué', '0.01')}
            <label className="block">
              <span className="block text-[11px] font-medium text-neutral-600 mb-1">Taux d’économie (%)</span>
              <input type="number" step="0.5" value={String(Math.round(draft.savingsRate * 1000) / 10)} onChange={e => set('savingsRate', Number(e.target.value) / 100)}
                className={`${inputClass} ${errors.savingsRate ? 'border-red-300' : 'border-neutral-200'}`} />
              <span className={`block text-[10px] mt-1 ${errors.savingsRate ? 'text-red-600' : 'text-neutral-400'}`}>{errors.savingsRate ?? 'Part de la facture économisée'}</span>
            </label>
            {field('roundTo', 'Arrondi (€)', 'Ex. 5 : 182 € devient 180 €', '1')}
          </div>
          <label className="block">
            <span className="block text-[11px] font-medium text-neutral-600 mb-1">Phrase ajoutée à l’e-mail du contrat</span>
            <textarea rows={3} value={draft.sentence} onChange={e => set('sentence', e.target.value)}
              className={`${inputClass} text-[13px] leading-relaxed ${errors.sentence ? 'border-red-300' : 'border-neutral-200'}`} />
            <span className={`block text-[10px] mt-1 ${errors.sentence ? 'text-red-600' : 'text-neutral-400'}`}>
              {errors.sentence ?? `Variables : ${SAVINGS_VARIABLES.map(v => `${v.key} (${v.label})`).join(' · ')}. Placée avant la formule de politesse ; si le message « Envoi du contrat » contient {{economie_estimee}}, la phrase n’est pas ajoutée.`}
            </span>
          </label>
          <div className="space-y-1">
            <p className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Exemples</p>
            <ul className="text-xs text-neutral-700 space-y-1">
              {examples.map(x => (
                <li key={x.label}>
                  {x.label} : <b>{x.estimate ? `${formatEuros(x.estimate.annualSavings)} par an` : '—'}</b>
                </li>
              ))}
            </ul>
            {examples[0]?.estimate && (
              <p className="text-[11px] text-neutral-500 italic">« {renderSavingsSentence(draft.sentence, examples[0].estimate)} »</p>
            )}
          </div>
        </div>
      )}
    </SettingsSection>
  );
}
